import { useState, useEffect, useRef } from 'react';

const usePageLoadProgress = (fetchDataFunction) => {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isDataReady, setIsDataReady] = useState(false);
  const [apiDataResults, setApiDataResults] = useState(null);

  const animationFrameRef = useRef(null);
  const highestProgressRef = useRef(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingProgress(0);
      setIsDataReady(false);
      setApiDataResults(null);
      highestProgressRef.current = 0;

      let currentProgress = 0;

      const updateProgress = (newProgress) => {
        if (newProgress > currentProgress) {
          currentProgress = newProgress;
          const clampedProgress = Math.min(currentProgress, 95);

          if (clampedProgress > highestProgressRef.current) {
            highestProgressRef.current = clampedProgress;
            setLoadingProgress(clampedProgress);
          } else {
            setLoadingProgress(highestProgressRef.current);
          }
        }
      };

      updateProgress(5);

      const fetchedData = await fetchDataFunction((progress) => {
        const overallProgress = 5 + progress * 90;
        updateProgress(overallProgress);
      });

      updateProgress(95);

      const duration = 300;
      let startTime = null;
      const targetProgress = 100;
      const finalKnownProgress = Math.min(highestProgressRef.current, 95);

      const animateFill = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const elapsed = currentTime - startTime;
        const progressFraction = Math.min(elapsed / duration, 1);

        let currentAnimatedProgress =
          finalKnownProgress +
          (targetProgress - finalKnownProgress) * progressFraction;
        currentAnimatedProgress = Math.min(currentAnimatedProgress, 100);

        setLoadingProgress(currentAnimatedProgress);

        if (progressFraction < 1 || currentAnimatedProgress < 100) {
          animationFrameRef.current = requestAnimationFrame(animateFill);
        } else {
          setApiDataResults([fetchedData]);
          setIsDataReady(true);
          animationFrameRef.current = null;
        }
      };

      animationFrameRef.current = requestAnimationFrame(animateFill);
    };

    fetchData();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return { loadingProgress, isDataReady, apiDataResults };
};

export default usePageLoadProgress;
